import type { WorkOrdersSquare } from 'types/squares.type';
import type { DownTimeAssetDetails, IdlingEvent } from './types';
/**
 * MTBF Result interface
 */
interface MTBFResult {
  asset_id: string;
  mtbf_days: number | null;
  failure_count: number;
  make?: string;
  model?: string;
  customer?: string;
}

/**
 * Calculate MTBF (Mean Time Between Failures) for each asset
 * @param workOrders - Array of work order objects
 * @returns Array of objects with asset_id and mtbf_days
 */
export function calculateMTBF(workOrders: WorkOrdersSquare[]): MTBFResult[] {
  const groupedByAsset = workOrders.reduce<Record<string, WorkOrdersSquare[]>>(
    (acc, order) => {
      const assetId = order.asset_id;
      if (!acc[assetId]) {
        acc[assetId] = [];
      }
      acc[assetId].push(order);
      return acc;
    },
    {}
  );

  const mtbfResults: MTBFResult[] = Object.entries(groupedByAsset).map(
    ([assetId, orders]) => {
      orders.sort(
        (a, b) =>
          new Date(a.date_of_orders).getTime() -
          new Date(b.date_of_orders).getTime()
      );

      const failureCount = orders.length;
      let mtbf: number | null = null;

      if (failureCount === 0) {
        mtbf = null;
      } else if (failureCount === 1) {
        const failureDate = new Date(orders[0].date_of_orders);
        const today = new Date();
        mtbf = Math.floor(
          (today.getTime() - failureDate.getTime()) / (1000 * 60 * 60 * 24)
        );
      } else {
        const tbfValues: number[] = [];
        for (let i = 1; i < orders.length; i++) {
          const currentDate = new Date(orders[i].date_of_orders);
          const previousDate = new Date(orders[i - 1].date_of_orders);
          const tbf = Math.floor(
            (currentDate.getTime() - previousDate.getTime()) /
              (1000 * 60 * 60 * 24)
          );
          if (tbf > 0) {
            tbfValues.push(tbf);
          }
        }

        if (tbfValues.length > 0) {
          const sum = tbfValues.reduce((acc, val) => acc + val, 0);
          mtbf = sum / tbfValues.length;
        } else {
          mtbf = 0;
        }
      }

      return {
        asset_id: assetId,
        mtbf_days: mtbf,
        failure_count: failureCount,
      };
    }
  );

  return mtbfResults.sort((a, b) => {
    if (a.mtbf_days === null) return 1;
    if (b.mtbf_days === null) return -1;
    return a.mtbf_days - b.mtbf_days;
  });
}

interface ComponentMTBFResult {
  asset_id: string;
  component: string;
  sub_component: string;
  mtbfDays: number | null;
  mtbfRepairs: number | null;
  mtbfReplace: number | null;
  failure_count: number;
  repair_count: number;
  replace_count: number;
  make?: string;
  model?: string;
  customer?: string;
}

function calculateMTBFFromOrders(orders: WorkOrdersSquare[]): number | null {
  if (orders.length === 0) {
    return null;
  } else if (orders.length === 1) {
    const failureDate = new Date(orders[0].date_of_orders);
    const today = new Date();
    return Math.floor(
      (today.getTime() - failureDate.getTime()) / (1000 * 60 * 60 * 24)
    );
  } else {
    const tbfValues: number[] = [];
    for (let i = 1; i < orders.length; i++) {
      const currentDate = new Date(orders[i].date_of_orders);
      const previousDate = new Date(orders[i - 1].date_of_orders);
      const tbf = Math.floor(
        (currentDate.getTime() - previousDate.getTime()) / (1000 * 60 * 60 * 24)
      );
      tbfValues.push(tbf);
    }

    const sum = tbfValues.reduce((acc, val) => acc + val, 0);
    return Math.round(sum / tbfValues.length);
  }
}

/**
 * Calculate MTBF for each unique asset + component + sub-component combination
 * @param workOrders - Array of work order objects from the database (already filtered by date)
 * @returns Array of objects with asset_id, component, sub_component, and all MTBF metrics
 */
export function calculateComponentMTBF(
  workOrders: WorkOrdersSquare[]
): ComponentMTBFResult[] {
  interface GroupData {
    asset_id: string;
    component: string;
    sub_component: string;
    allOrders: WorkOrdersSquare[];
    repairOrders: WorkOrdersSquare[];
    replaceOrders: WorkOrdersSquare[];
  }

  const groupedData = workOrders.reduce<Record<string, GroupData>>(
    (acc, order) => {
      const component = order.components_category || 'UNKNOWN';

      let subComponents: string[] = [];
      try {
        const cleaned = order.sub_component_categories
          .replace(/[[\]]/g, '')
          .trim();
        subComponents = cleaned
          ? cleaned.split(',').map((s) => s.trim())
          : ['UNKNOWN'];
      } catch {
        subComponents = ['UNKNOWN'];
      }

      const action = order.actions_performed?.toUpperCase() || 'UNKNOWN';

      subComponents.forEach((subComponent) => {
        const compositeKey = `${order.asset_id}|${component}|${subComponent}`;

        if (!acc[compositeKey]) {
          acc[compositeKey] = {
            asset_id: order.asset_id,
            component,
            sub_component: subComponent,
            allOrders: [],
            repairOrders: [],
            replaceOrders: [],
          };
        }

        acc[compositeKey].allOrders.push(order);

        if (action === 'REPAIR') {
          acc[compositeKey].repairOrders.push(order);
        } else if (action === 'REPLACE') {
          acc[compositeKey].replaceOrders.push(order);
        }
      });

      return acc;
    },
    {}
  );

  const mtbfResults: ComponentMTBFResult[] = Object.values(groupedData).map(
    (data) => {
      data.allOrders.sort(
        (a, b) =>
          new Date(a.date_of_orders).getTime() -
          new Date(b.date_of_orders).getTime()
      );
      data.repairOrders.sort(
        (a, b) =>
          new Date(a.date_of_orders).getTime() -
          new Date(b.date_of_orders).getTime()
      );
      data.replaceOrders.sort(
        (a, b) =>
          new Date(a.date_of_orders).getTime() -
          new Date(b.date_of_orders).getTime()
      );

      const mtbfDays = calculateMTBFFromOrders(data.allOrders);
      const mtbfRepairs = calculateMTBFFromOrders(data.repairOrders);
      const mtbfReplace = calculateMTBFFromOrders(data.replaceOrders);

      return {
        asset_id: data.asset_id,
        component: data.component,
        sub_component: data.sub_component,
        label: `${data.component} - ${data.sub_component}`,
        mtbfDays,
        mtbfRepairs,
        mtbfReplace,
        failure_count: data.allOrders.length,
        repair_count: data.repairOrders.length,
        replace_count: data.replaceOrders.length,
      };
    }
  );

  return mtbfResults.sort((a, b) => {
    if (a.mtbfDays === null) return 1;
    if (b.mtbfDays === null) return -1;
    return b.mtbfDays - a.mtbfDays;
  });
}

export function diffInHours(
  date1: Date | string,
  date2: Date | string
): number {
  const d1 = typeof date1 === 'string' ? new Date(date1) : date1;
  const d2 = typeof date2 === 'string' ? new Date(date2) : date2;

  const diffMs = d2.getTime() - d1.getTime();
  const hours = diffMs / (1000 * 60 * 60);

  return hours;
}

export function getAssetLevelMetrics(
  assetDetails: MTBFResult[],
  startDate: Date,
  endDate: Date,
  workOrders: WorkOrdersSquare[],
  downtimeData: DownTimeAssetDetails[],
  idlingData: IdlingEvent[]
) {
  const totalTimeHours = diffInHours(startDate, endDate);

  const withMetrics = assetDetails.map((item) => {
    const assetWorkOrders = workOrders.filter(
      (wo) => wo.asset_id === item.asset_id
    );
    const assetDownTime = downtimeData.filter(
      (dt) => dt.asset_id === item.asset_id
    );

    const assetIdlingDuration =
      idlingData
        .filter((idling) => idling.asset_id === item.asset_id)
        .reduce((accumulator: number, currentValue: IdlingEvent) => {
          return accumulator + currentValue.total_duration;
        }, 0) /
      (60 * 60);

    const totalFailures = assetWorkOrders.length;
    const failureRate =
      totalTimeHours === 0 ? 0 : (totalFailures / totalTimeHours) * 100;

    const totalDownTimeHours = Math.min(
      assetDownTime.reduce(
        (accumulator: number, currentValue: DownTimeAssetDetails) => {
          return accumulator + currentValue.duration;
        },
        0
      ) /
        (60 * 60),
      totalTimeHours
    );

    const totalUptimeHours = totalTimeHours - totalDownTimeHours;
    const availability =
      totalTimeHours === 0 ? 0 : (totalUptimeHours / totalTimeHours) * 100;

    const utilization =
      totalUptimeHours === 0
        ? assetIdlingDuration > 0
          ? 100
          : 0
        : (assetIdlingDuration / totalUptimeHours) * 100;

    return { ...item, failureRate, availability, utilization };
  });
  return withMetrics;
}
